import { CollaboratorType } from 'constant/Constants';
import DataType from 'constant/DataType';
import { TransactionReason } from 'domain/stream/transaction/TransactionDomains';
import { AccessBase } from '../access/BaseAccess';
import { PermissionType } from '../rbac/RbacDomains';
import { BackgroundJob } from '../job/JobDomain';
import { query } from '@trlogic/client-js';
import { SuspiciousActivityReport } from './SuspiciousActivityReport';
import { FormicaTimePickerDate } from '../../components/custom/custom-time-picker/TimePickerDomain';
import Filter = query.Filter;

export interface CaseSettings extends AccessBase {
  id?: string;
  name: string;
  description?: string;
  dataSource: string;
  keyField: string;
  panels: Array<CaseFieldPanel | CaseChartPanel>;
  graphConfiguration: CaseGraphField[];
  closeCodes: Array<AlarmCloseCode>;
  blacklists: Array<string>;
  transactionAmountField: string | undefined;
  transactionCurrencyField: string | undefined;
  suspiciousActivityReport: SuspiciousActivityReport;
  sla: {
    timeToDone: {
      type: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
      value: number | undefined;
    };
    timeToFirstAssign: {
      type: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
      value: number | undefined;
    };
  };
  actions: CaseAction[];
  assignmentCandidates: AssignmentCandidates[];
  assignmentCandidatesRequired: boolean;
  graphDefaultFilterRequired: boolean;
  graphDefaultFilter: GraphDefaultFilter;
  goOldestCase: boolean;
  allowAssign?: boolean | null;
  allowAssignUsers?: string[] | null;
  bulkCloseRestrictedUsers?: string[];
  actionRequired: boolean;
  reasonRequired: boolean;
  additionalChannels: CaseAdditionalChannel[];
  entities: Entity[];
}

export interface Entity {
  name: string;
  description: string;
  key: string;
  variables: string[];
}

export interface GraphDefaultFilter {
  startDate: FormicaTimePickerDate | undefined;
  endDate: FormicaTimePickerDate | undefined;
  field: string | undefined;
}
export enum CasePanelType {
  PANEL = 'PANEL',
  CHART = 'CHART',
}

export interface CasePanel {
  title: string;
  enabled: boolean;
  gridType: 'even' | 'odd';
  type: CasePanelType;
}

export interface CaseFieldPanel extends CasePanel {
  fields: CasePanelField[];
}

export interface CaseChartPanel extends CasePanel {
  selectedDashboard: string;
  selectedChart: string;
  chartFilterField: {
    value: string;
    dataType: DataType;
  };
  filterWith: {
    value: string;
    dataType: DataType;
  };
}

export interface CasePanelField {
  title: string;
  value: string;
  type: string;
  key: string;
}

export interface CaseGraphField {
  color?: string;
  dataType?: DataType;
  description?: string;
  enabled?: boolean;
  field: string;
  icon?: string;
  label: string;
}

export interface AlarmCollaborator {
  type: CollaboratorType;
  name: string;
  role: PermissionType;
}

export type AlarmCloseCodeStatus = 'FRAUD' | 'NOT_FRAUD' | 'COMPLIANT' | 'IN_COMPLIANT' | 'HOLD';

export interface AlarmCloseCode {
  code: string;
  status: AlarmCloseCodeStatus;
}

export enum CaseActionStatus {
  ALL = 'ALL',
  FRAUD = 'FRAUD',
  NOT_FRAUD = 'NOT_FRAUD',
}

export interface CaseAction {
  name: string;
  status?: CaseActionStatus;
}

export interface CaseEventLog {
  id: string;
  type: 'COMMENT' | 'COMMENT_EDIT' | 'ATTACHMENT' | 'CLOSE' | 'GO' | 'ASSIGNEE' | 'TAGGED_FIELD' | 'UNASSIGMENT' | 'HOLD' | 'ACTION' | 'LIST_ADD' | 'LIST_REMOVE';
  transactionId: string;
  username: string;
  domain: string;
  timestamp: number;
  ip: string;
  protocol?: string;
  port?: number;
}

export interface CaseEventReference {
  transactionId: string;
  domain: string;
}

export type CaseCloseType = 'BULK' | 'SINGLE' | 'API';

export interface CaseActionHistoryItem {
  type: CaseEventLog['type'];
  timestamp: number;
  username: string;
  status?: string;
  action?: string;
  success?: boolean;
  list?: string;
  value?: string;
  bulk?: boolean;
  closeType?: CaseCloseType;
}

export interface CaseActionHistory {
  firstActionAt: number;
  actions: CaseActionHistoryItem[];
}

export interface CaseHoldAction extends CaseEventLog {
  code?: string;
}

export interface TransactionChatLog {
  id: string;
  type: 'COMMENT' | 'ATTACHMENT';
  username: string;
  domain: string;
  timestamp: number;
  content?: string;
  attachments?: CaseAttachment[];
}

export interface CaseCommentLog extends CaseEventLog {
  content: string;
}

export interface CaseAttachmentLog extends CaseEventLog {
  attachments: CaseAttachment[];
}

export interface CaseTaggedFieldLog extends CaseEventLog {
  taggedField: string;
  taggedValue: string;
  channel: string;
}

export interface CaseAttachment {
  id: string;
  mimetype: string;
  fileName: string;
  url: string;
  size: number;
}

export interface RuleDashboardArgs {
  transaction?: string;
  dateField?: string;
  dateValue: string;
  useCache?: boolean;
}

export interface BatchCloseCaseJob extends BackgroundJob {
  domain: string;
  caseStatus: TransactionReason.FRAUD | TransactionReason.NOT_FRAUD | TransactionReason.IN_COMPLIANT | TransactionReason.COMPLIANT;
  closeCode?: string;
  filter: Filter;
}

export interface CloseCaseBulkActionOperation {
  id: string;
  index: string;
}

export interface CaseAdditionalChannel {
  channel: string;
  field: string;
  keyField?: string;
}

export interface AssignmentCandidates {
  username: string;
  riskType: AssignmentCandidatesRiskType;
}

export enum AssignmentCandidatesRiskType {
  FRAUD = 'FRAUD',
  COMPLIANCE = 'COMPLIANCE',
  ALL = 'ALL',
}
